<template lang="pug">
  .chosen-methodologies.column
    h3.title-medium.m-bottom-3
      | {{ $t('stats.chosenMethodologies.title') }}
    .chart-card.column
      .chart.row.space-around.middle
        .chart-container.row.center.middle.relative
          .no-info-message.title-medium.slim(v-if='!methodologies.length')
            | {{ $t('stats.chosenMethodologies.noInfo') }}
          loader.loader.absolute(v-if='isLoading' size='inner' whiteBackground)
          .amchart(ref='chartdiv')
        .column.m-right-3
          ul.legend
            li.item(v-for='methodology in methodologyOptions' :style='bulletStyle(methodology.color)')
              span.item-description.dark-gray
                | {{ $t(`stats.chosenMethodologies.methodologies.${methodology.name}`) }}
              span.item-value
                | {{ methodologyValue(methodology.name) }}%
      .card-footer.row
        .filter-select
          span.label.dark-gray
            | {{ $t('stats.chartOptions.time') }}
          k-select.time-filter.m-top-1(
            name='startingDate'
            :options='getTranslatedOptions(timeRangesOptions)'
            keyName='value'
            :preselectedOption='filters.startingDate'
            noSpacing
            @selectOption='updateFilter'
          )
        .filter-select.m-left-5
          span.label.dark-gray
            | {{ $t('stats.chartOptions.category') }}
          k-select.category-filter.m-top-1(
            name='categoryId'
            :options='getTranslatedOptions(categoryOptions)'
            keyName='value'
            :preselectedOption='filters.categoryId'
            :emptyOption='$t("stats.allCategories")'
            noSpacing
            @selectOption='updateFilter'
          )
        .filter-select.m-left-5(v-if='isAdminUser || isTrial')
          span.label.dark-gray
            | {{ $t('stats.chartOptions.school') }}
          k-select.school-filter.m-top-1(
            name='schoolId'
            :options='schools'
            keyName='value'
            :emptyOption='$t("stats.allSchools")'
            noSpacing
            @selectOption='updateFilter'
          )
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import amplitude from 'amplitude-js'

import KSelect from '@/components/KSelect'
import Loader from '@/components/Loader'
import stats from '../../mixins/stats'
import userRole from '../../mixins/userRole'

import { pieChartConfiguration } from '@/config/charts/pieChartConfig'
import { methodologies, emptyGraphic } from '../../utils/constants'
import { roundToTwo } from '../../utils/round'

export default {
  mixins: [stats, userRole],
  data() {
    return {
      emptyGraphic,
      methodologyOptions: methodologies,
      filters: {
        startingDate: 1,
        schoolId: null,
        categoryId: null
      }
    }
  },
  mounted() {
    this.updateChart()
  },
  computed: {
    ...mapGetters({ methodologies: 'getMappedChosenMethodologies' }),
    ...mapState({
      isLoading: state => state.statistics.chosenMethodologiesLoading
    }),
    colors() {
      return this.methodologyOptions.map(e => e.color)
    }
  },
  methods: {
    ...mapActions([ 'getChosenMethodologies' ]),
    bulletStyle(color) {
      return `--bullet-color: ${color};`
    },
    async updateChart() {
      amplitude.getInstance().logEvent('Filter Statistics', {
        chartName: 'Chosen Methodologies',
        filters: this.filters
      })
      await this.getChosenMethodologies(this.filters)
      const isEmpty = !this.methodologies.length
      pieChartConfiguration(this.$refs.chartdiv, isEmpty, isEmpty ? this.emptyGraphic : this.methodologies, this.colors)
    },
    methodologyValue(methodologyName) {
      const methodology = this.methodologies.find(e => (methodologyName === e.category))
      return methodology ? roundToTwo(methodology.value) : '-'
    }
  },
  components: { KSelect, Loader }
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables/colors';

.chosen-methodologies {
  flex: 1;
}

.chart-card {
  border: solid 1px $light-gray;
  box-shadow: 0 2px 8px $shadow-gray;
  border-radius: 10px;
  width: 700px;
  flex: 1;
}

.chart-container {
  position: relative;
}

.no-info-message {
  position: absolute;
  text-align: center;
  z-index: 1;
}

.chart {
  flex: 1;
}

.amchart {
  height: 460px;
  width: 460px;
}

.legend {
  --bullet-color: $black;
  .item {
    margin-bottom: 24px;

    &::before {
      content: '';
      display: inline-block;
      height: 7px;
      width: 7px;
      border-radius: 100%;
      background-color: var(--bullet-color);
      margin-right: 6px;
    }
  }

  .item-description {
    font-size: 12px;
    margin-bottom: 6px;
  }

  .item-value {
    display: block;
    font-size: 24px;
    padding-left: 12px;
  }
}

.card-footer {
  border: 1px solid $light-gray;
  padding: 10px 30px;
}

.filter-select {
  .label {
    font-size: 12px;
  }
}

.time-filter {
  min-width: 140px;
}

.category-filter {
  min-width: 230px;
}

.school-filter {
  min-width: 160px;
}
</style>
